import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { trackLogin } from '../utils/analytics';
import {
  clearAccessToken,
  clearActiveAuthToken,
  clearActiveMode,
  getAuthToken,
  isAnonDemoOnly,
  setAccessToken,
} from '../utils/authStorage';
import {
  AUTH_FORCED_SIGN_OUT_EVENT,
  clearPrivateSessionStorage,
} from '../utils/authLifecycleCleanup';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuthScopedClientState = useCallback(() => {
    queryClient.clear();
    clearPrivateSessionStorage();
  }, [queryClient]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const handleForcedSignOut = () => {
      clearAuthScopedClientState();
      clearActiveMode();
      setIsAuthenticated(false);
      setUser(null);
    };

    window.addEventListener(AUTH_FORCED_SIGN_OUT_EVENT, handleForcedSignOut);
    return () => window.removeEventListener(AUTH_FORCED_SIGN_OUT_EVENT, handleForcedSignOut);
  }, [clearAuthScopedClientState]);

  const checkAuth = async () => {
    // Anon-demo isolation (a /play demo tab): always present as anon, never
    // resolve a real identity. Mirrors the verified logged-out demo flow
    // (isAuthenticated=false) even if a real access_token sits in localStorage,
    // so a logged-in visitor opening the share link gets the clean anon demo
    // instead of their own dashboard. Released the moment they truly sign in.
    if (isAnonDemoOnly()) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }

    // Tutorial test mode: sessionStorage token takes precedence (tab-isolated)
    const token = getAuthToken();
    try {
      if (token) {
        try {
          const response = await apiClient.get('/api/auth/me', { timeout: 10000 });
          setUser(response.data);
          setIsAuthenticated(true);
          return;
        } catch (error) {
          // In tutorial test mode, only clear sessionStorage — don't touch localStorage
          clearActiveAuthToken();
          clearAuthScopedClientState();
        }
      }
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (emailOrUsername, password) => {
    try {
      // OAuth2PasswordRequestForm expects application/x-www-form-urlencoded
      const params = new URLSearchParams();
      params.append('username', emailOrUsername);
      params.append('password', password);
      const response = await apiClient.post('/api/auth/login', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: 60000, // Explicit 60s timeout for login
      });
      clearAuthScopedClientState();
      setAccessToken(response.data.access_token);
      await checkAuth();
      trackLogin('email');
      return { success: true };
    } catch (error) {
      // Provide better error messages based on error type
      let errorMessage = 'Login failed';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection and try again.';
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.response) {
        // Server responded with an error
        const status = error.response.status;
        if (status === 401) {
          errorMessage = error.response.data?.detail || 'Incorrect email/username or password';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (status >= 400 && status < 500) {
          errorMessage = error.response.data?.detail || 'Invalid request. Please check your input.';
        } else {
          errorMessage = error.response.data?.detail || 'An error occurred. Please try again.';
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await apiClient.post('/api/auth/google', {
        credential: credential,
      });
      clearAuthScopedClientState();
      setAccessToken(response.data.access_token);
      await checkAuth();
      trackLogin('google');
      return { success: true };
    } catch (error) {
      // Provide better error messages based on error type
      let errorMessage = 'Google sign-in failed';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Connection timeout. Please check your internet connection and try again.';
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.response) {
        // Server responded with an error
        const status = error.response.status;
        if (status === 401) {
          errorMessage = error.response.data?.detail || 'Invalid Google credential';
        } else if (status === 400) {
          errorMessage = error.response.data?.detail || 'Google sign-in failed. Please try again.';
        } else if (status === 500 || status === 501) {
          errorMessage = 'Server error. Please try again later.';
        } else if (status >= 400 && status < 500) {
          errorMessage = error.response.data?.detail || 'Invalid request. Please check your input.';
        } else {
          errorMessage = error.response.data?.detail || 'An error occurred. Please try again.';
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const logout = () => {
    clearAuthScopedClientState();
    clearAccessToken();
    clearActiveMode();
    setIsAuthenticated(false);
    setUser(null);
    // Clear refresh token cookie on backend
    apiClient.post('/api/auth/logout').catch(() => {});
  };

  // Optimistic local user patch — used by surfaces that mutate user state
  // server-side and want to update the React tree without waiting for a
  // /api/auth/me round-trip. Shallow-merges top-level keys; for nested
  // objects (e.g. settings) the caller is responsible for spreading.
  const patchUser = (partial) => {
    setUser((current) => (current ? { ...current, ...partial } : current));
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      loading,
      login,
      loginWithGoogle,
      logout,
      checkAuth,
      patchUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
